import { useState, useEffect, useCallback } from "preact/hooks";
import axios from "axios";

export const useConsumers = () => {
    const [consumers, setConsumers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        hasNext: false,
        hasPrev: false
    });
    const fetchConsumers = useCallback(async (params={}) => {
        try {
            setLoading(true);
            const queryParams = {
                limit: (params.pageSize || pagination.pageSize) + 1, // Request one extra to check if there's next page
                offset: ((params.current || pagination.current) - 1) * (params.pageSize || pagination.pageSize),
                username: params.username || '',
                status: (params.status && params.status !== 'all') ? params.status : '',
            };
            const response = await axios.get("/admin/consumers", { params: queryParams });
            const data = response.data;
            const pageSize = params.pageSize || pagination.pageSize;
            const currentPage = params.current || pagination.current;

            const hasNext = data.length > pageSize;
            const actualData = hasNext ? data.slice(0, pageSize) : data;

            setConsumers(actualData);
            setPagination({
                current: currentPage,
                pageSize: pageSize,
                hasNext: hasNext,
                hasPrev: currentPage > 1
            });
            setError(null);
        } catch (err) {
            console.error("Error fetching consumers:", err);
            setError("Failed to load consumers. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, []);

    const createConsumer = useCallback(async (consumerData) => {
        try {
            setLoading(true);
            const response = await axios.post("/admin/consumers", consumerData);
            setError(null);
            return response.data;
        } catch (err) {
            console.error("Error creating consumer:", err);
            setError("Failed to create consumer. Please try again.");
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const getConsumerByConsumerId = useCallback(async (consumerId) => {
        try {
            setLoading(true);
            const response = await axios.get(`/admin/consumer/${consumerId}`);
            setError(null);
            return response.data;
        } catch (err) {
            console.error("Error getting consumer by consumerID:", err);
            setError("Failed to get consumer. Please try again.");
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const changeConsumerStatus = useCallback(async (consumerId, newStatus) => {
        try {
            setLoading(true);
            const response = await axios.post(`/admin/consumer/${consumerId}/status`, {
                status: newStatus
            });
            setError(null);
            return response.data;
        } catch (error) {
            console.error("Error getting consumer by consumerID:", error);
            setError("Failed to get consumer. Please try again.");
            throw error;
        } finally {
            setLoading(false);
        }
    })

    const resetConsumerPassword = useCallback(async (updateConsumerData) => {
        try {
            setLoading(true);
            const response = await axios.post(`/admin/reset_password`, updateConsumerData)
            setError(null);
            return response.data;
        } catch (error){
            console.error("Error resetting password for consumer:", error);
            setError("Failed to update the password. Please try again.");
            throw error;
        } finally {
            setLoading(false);
        }
    })

    const updateConsumerContact = useCallback(async (consumerId, values) => {
        try {
            setLoading(true);
            const response = await axios.put(`/admin/consumer/${consumerId}`, {
                address: values.address,
                country: values.country,
                city: values.city,
                contact_person_email: values.contact_person_email,
                contact_person_full_name: values.contact_person_full_name,
                contact_person_phone_number: values.contact_person_phone_number,
                company: values.company
            })
            setError(null);
            return response.data;
        } catch(error){
            console.error("Error editing consumer by consumerID:", error);
            setError("Failed to update the consumer. Please try again.");
            throw error;
        } finally {
            setLoading(false);
        }
    })

    useEffect(() => {
        fetchConsumers();
    }, []);

    return {
        changeConsumerStatus,
        consumers,
        createConsumer,
        getConsumerByConsumerId,
        resetConsumerPassword,
        updateConsumerContact,
        loading,
        error,
        pagination,
        fetchConsumers
    };
};